import { lentComponent, useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { LentToken, MutationResult, QueryResult } from "@amtana/browser";
import { organizationTrpcContract } from "@amtana/organization-contract";
import type {
  AcceptInviteInput,
  AcceptInviteOutput,
  CreateOrganizationInput,
  DeclineInviteInput,
  InvitationIdInput,
  InvitationLinkOutput,
  InvitationsOutput,
  InviteMemberInput,
  MembersOutput,
  Membership,
  MyOrganizationsOutput,
  Ok,
  Organization,
  RemoveMemberInput,
  RenameOrganizationInput,
  RestoreOrganizationInput,
  SetMemberRoleInput,
  SetOrganizationLogoInput,
} from "@amtana/organization-contract";

// The members directory (S3.02) and the three ways it changes, guarded
// against ever leaving the organization without an admin (S3.03); and
// invitations (S3.04, S3.05, S3.06): sending one, the pending list, resend,
// copy link, revoke, accept and decline. Every write invalidates the whole
// contract: changing or removing someone, or spending an invite, changes
// what the caller's own next request may do (S4.03).

export function useMembers(): QueryResult<MembersOutput> {
  return useContractQuery(organizationTrpcContract, "members", { input: {} });
}

/** The switcher's own list (S1.05): every organization the caller belongs to. */
/** `optional` for a caller that also renders for nobody signed in (the flags source): a refused
 * read stays on the result instead of replacing the screen. */
export function useMyOrganizations(
  options: { readonly optional?: boolean } = {},
): QueryResult<MyOrganizationsOutput> {
  return useContractQuery(organizationTrpcContract, "myOrganizations", {
    input: {},
    ...(options.optional ? { optional: true } : {}),
  });
}

/** Admin-only on the door (`invitations:read`); pass `enabled: false` for a
 * non-admin so the screen never asks and never hits the forbidden boundary. */
export function useInvitations(options: { readonly enabled?: boolean } = {}): QueryResult<InvitationsOutput> {
  return useContractQuery(organizationTrpcContract, "invitations", {
    input: {},
    ...(options.enabled !== undefined ? { enabled: options.enabled } : {}),
  });
}

type WriteName =
  | "create"
  | "rename"
  | "setLogo"
  | "restoreOrganization"
  | "setMemberRole"
  | "removeMember"
  | "leave"
  | "inviteMember"
  | "resendInvite"
  | "copyInviteLink"
  | "revokeInvite"
  | "acceptInvite"
  | "declineInvite";

function useWrite<TInput, TOutput>(name: WriteName): MutationResult<TOutput, TInput> {
  const mutation = useContractMutation(organizationTrpcContract, name);
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<TOutput, TInput>;
  return {
    ...typed,
    run: (input: TInput) => typed.mutateAsync(input).finally(() => void invalidate(organizationTrpcContract)),
  };
}

export const useCreateOrganization = (): MutationResult<Organization, CreateOrganizationInput> =>
  useWrite("create");
export const useRenameOrganization = (): MutationResult<Organization, RenameOrganizationInput> =>
  useWrite("rename");
export const useSetOrganizationLogo = (): MutationResult<Organization, SetOrganizationLogoInput> =>
  useWrite("setLogo");
// D307: deleting an organization is no longer a customer's own action (removed from
// Settings); `useRequestOrganizationDeletion` is gone with it. `useRestoreOrganization`
// stays: it still undoes a pending deletion auth's own account-deletion cascade (S6.10)
// started, or one the back office started, inside the grace period.
export const useRestoreOrganization = (): MutationResult<Organization, RestoreOrganizationInput> =>
  useWrite("restoreOrganization");

export const useSetMemberRole = (): MutationResult<Membership, SetMemberRoleInput> =>
  useWrite("setMemberRole");
export const useRemoveMember = (): MutationResult<Ok, RemoveMemberInput> => useWrite("removeMember");
export const useLeaveOrganization = (): MutationResult<Ok, Record<string, never>> => useWrite("leave");

export const useInviteMember = (): MutationResult<InvitationLinkOutput, InviteMemberInput> =>
  useWrite("inviteMember");
export const useResendInvite = (): MutationResult<InvitationLinkOutput, InvitationIdInput> =>
  useWrite("resendInvite");
export const useCopyInviteLink = (): MutationResult<InvitationLinkOutput, InvitationIdInput> =>
  useWrite("copyInviteLink");
export const useRevokeInvite = (): MutationResult<Ok, InvitationIdInput> => useWrite("revokeInvite");
export const useAcceptInvite = (): MutationResult<AcceptInviteOutput, AcceptInviteInput> =>
  useWrite("acceptInvite");
export const useDeclineInvite = (): MutationResult<Ok, DeclineInviteInput> => useWrite("declineInvite");

/** The shell's top bar: the active organization, every other the caller belongs
 * to, switching and creating one. */
export const OrganizationSwitcher: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "organization",
  "OrganizationSwitcher",
);
